import {
  CameraOutlined,
  LockOutlined,
  SafetyCertificateOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  AdminPage,
  Alert,
  App,
  Button,
  Card,
  Descriptions,
  Form,
  Input,
  Space,
  Tag,
  Typography,
  Upload,
} from '@wanjianshi/admin-ui';
import { useState } from 'react';
import { useAdminAuth } from '@/auth/store';
import { UserAvatar } from '@/components/UserAvatar';
import { changeMyPassword, updateMyAvatar } from '@/services/api/modules/profile';
import { formatAdminRoleNames } from '@/utils/adminRole';

interface PasswordFormValues {
  current_password: string;
  new_password: string;
  confirm_password: string;
}

function validAvatar(file: File): string | null {
  if (!file.type.startsWith('image/') && !/\.(png|jpe?g|webp|heic|heif)$/i.test(file.name)) {
    return '头像仅支持 PNG、JPG、WEBP、HEIC 或 HEIF 图片';
  }
  if (file.size > 5 * 1024 * 1024) return '头像文件不能超过 5MB';
  return null;
}

export function ProfilePage() {
  const { message } = App.useApp();
  const principal = useAdminAuth(state => state.principal);
  const updatePrincipal = useAdminAuth(state => state.updatePrincipal);
  const logout = useAdminAuth(state => state.logout);
  const [passwordForm] = Form.useForm<PasswordFormValues>();
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const roles = formatAdminRoleNames(
    principal?.role_names?.length ? principal.role_names : principal?.roles,
  );

  const uploadAvatar = async (file: File) => {
    const validationError = validAvatar(file);
    if (validationError) {
      message.error(validationError);
      return;
    }
    setAvatarUploading(true);
    try {
      const nextPrincipal = await updateMyAvatar(file);
      updatePrincipal(nextPrincipal);
      message.success('头像已更新');
    } catch (error) {
      message.error(error instanceof Error ? error.message : '头像更新失败');
    } finally {
      setAvatarUploading(false);
    }
  };

  const submitPassword = async () => {
    let values: PasswordFormValues;
    try {
      values = await passwordForm.validateFields();
    } catch {
      return;
    }
    setPasswordSaving(true);
    try {
      await changeMyPassword({
        current_password: values.current_password,
        new_password: values.new_password,
      });
      message.success('密码已修改，请使用新密码重新登录');
      passwordForm.resetFields();
      await logout();
    } catch (error) {
      message.error(error instanceof Error ? error.message : '密码修改失败');
    } finally {
      setPasswordSaving(false);
    }
  };

  return (
    <AdminPage
      title="个人中心"
      description="管理当前后台管理员的头像与登录密码"
    >
      <div className="admin-profile-layout">
        <Card className="admin-profile-card admin-profile-card--identity">
          <div className="admin-profile-avatar-wrap">
            <UserAvatar
              size={92}
              src={principal?.user.avatar || undefined}
              alt={principal?.user.nickname || principal?.user.username}
            />
            <Upload
              accept="image/png,image/jpeg,image/webp,image/heic,image/heif"
              showUploadList={false}
              beforeUpload={file => {
                void uploadAvatar(file);
                return false;
              }}
            >
              <Button
                icon={<CameraOutlined />}
                loading={avatarUploading}
                className="admin-profile-avatar-button"
              >
                更换头像
              </Button>
            </Upload>
          </div>
          <Typography.Title level={3} className="admin-profile-name">
            {principal?.user.nickname || principal?.user.username || '后台管理员'}
          </Typography.Title>
          <Typography.Text type="secondary">{principal?.user.username}</Typography.Text>
          <Space size={[6, 6]} wrap className="admin-profile-roles">
            {(roles.length ? roles : ['后台管理员']).map(role => (
              <Tag key={role} icon={<SafetyCertificateOutlined />} color="blue">{role}</Tag>
            ))}
          </Space>
          <Descriptions
            column={1}
            size="small"
            className="admin-profile-descriptions"
            items={[
              { key: 'id', label: '管理员 ID', children: principal?.user.id ?? '-' },
              { key: 'nickname', label: '账户昵称', children: principal?.user.nickname || '-' },
              { key: 'username', label: '登录账号', children: principal?.user.username || '-' },
            ]}
          />
          <Typography.Text type="secondary" className="admin-profile-upload-tip">
            建议上传正方形头像，支持 PNG、JPG、WEBP，最大 5MB。
          </Typography.Text>
        </Card>

        <Card className="admin-profile-card" title={<Space><LockOutlined />安全设置</Space>}>
          <Alert
            type="info"
            showIcon
            title="密码属于 system_users 平台账户"
            description="修改后，当前后台会话会退出；该账户下次登录 Web 端也需要使用新密码。后台 Token 与 Web Token 仍相互隔离。"
            className="admin-profile-password-alert"
          />
          <Form form={passwordForm} layout="vertical" className="admin-profile-password-form">
            <Form.Item
              name="current_password"
              label="当前密码"
              rules={[{ required: true, message: '请输入当前密码' }]}
            >
              <Input.Password autoComplete="current-password" placeholder="请输入当前登录密码" />
            </Form.Item>
            <Form.Item
              name="new_password"
              label="新密码"
              rules={[
                { required: true, message: '请输入新密码' },
                { min: 8, message: '新密码不能少于 8 个字符' },
                { max: 128, message: '新密码不能超过 128 个字符' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || value !== getFieldValue('current_password')) return Promise.resolve();
                    return Promise.reject(new Error('新密码不能与当前密码相同'));
                  },
                }),
              ]}
            >
              <Input.Password autoComplete="new-password" maxLength={128} placeholder="请输入 8–128 位新密码" />
            </Form.Item>
            <Form.Item
              name="confirm_password"
              label="确认新密码"
              dependencies={['new_password']}
              rules={[
                { required: true, message: '请再次输入新密码' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || value === getFieldValue('new_password')) return Promise.resolve();
                    return Promise.reject(new Error('两次输入的新密码不一致'));
                  },
                }),
              ]}
            >
              <Input.Password autoComplete="new-password" maxLength={128} placeholder="请再次输入新密码" />
            </Form.Item>
            <div className="admin-profile-password-actions">
              <Button type="primary" loading={passwordSaving} onClick={() => void submitPassword()}>
                修改密码
              </Button>
            </div>
          </Form>
        </Card>
      </div>
    </AdminPage>
  );
}
